import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Slide,
  IconButton,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  details?: string;
}

interface NotificationContextType {
  showNotification: (notification: Omit<NotificationData, 'id'>) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string, details?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);


interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  maxNotifications = 3 
}) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const showNotification = (notification: Omit<NotificationData, 'id'>) => {
    const newNotification: NotificationData = {
      id: generateId(),
      duration: 6000, // Default 6 seconds
      persistent: false,
      ...notification,
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      // Limit number of notifications
      return updated.slice(0, maxNotifications);
    });

    // Auto-remove non-persistent notifications
    if (!newNotification.persistent && newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, newNotification.duration);
    }
  };

  const showSuccess = (message: string, title?: string) => {
    showNotification({
      type: 'success',
      title: title || 'Success',
      message,
      duration: 4000,
    });
  };

  const showError = (message: string, title?: string, details?: string) => {
    showNotification({
      type: 'error',
      title: title || 'Error',
      message,
      details,
      duration: 8000, // Longer for errors
      persistent: false,
    });
  };

  const showWarning = (message: string, title?: string) => {
    showNotification({
      type: 'warning',
      title: title || 'Warning',
      message,
      duration: 6000,
    });
  };

  const showInfo = (message: string, title?: string) => {
    showNotification({
      type: 'info',
      title: title || 'Information',
      message,
      duration: 5000,
    });
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  // Listen for global notification events
  useEffect(() => {
    const handleGlobalNotification = (event: any) => {
      const { message, type = 'info', title, details } = event.detail || {};
      
      if (message) {
        showNotification({
          type,
          title,
          message,
          details,
        });
      }
    };

    // Listen for custom events dispatched throughout the app
    document.addEventListener('showNotification', handleGlobalNotification);
    
    return () => {
      document.removeEventListener('showNotification', handleGlobalNotification);
    };
  }, []);

  const contextValue: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Render notifications */}
      <Box sx={{ position: 'fixed', top: 80, right: 16, zIndex: 2000, maxWidth: 400 }}>
        <Stack spacing={1}>
          {notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClose={() => removeNotification(notification.id)}
            />
          ))}
        </Stack>
      </Box>
    </NotificationContext.Provider>
  );
};

// Individual notification component
interface NotificationItemProps {
  notification: NotificationData;
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Allow animation to complete
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Slide in={isVisible} direction="left" mountOnEnter unmountOnExit>
      <Alert
        severity={notification.type}
        variant="filled"
        icon={getIcon()}
        sx={{
          boxShadow: 4,
          borderRadius: 2,
          minWidth: 300,
          maxWidth: 400,
          '& .MuiAlert-message': {
            flexGrow: 1,
          },
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {notification.title && (
          <AlertTitle sx={{ mb: 1 }}>
            {notification.title}
          </AlertTitle>
        )}
        
        <Box>{notification.message}</Box>
        
        {/* Action button if provided */}
        {notification.action && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={notification.action.label}
              size="small"
              variant="outlined"
              onClick={notification.action.onClick}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'inherit',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
          </Box>
        )}

        {/* Details section */}
        {notification.details && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={showDetails ? 'Hide Details' : 'Show Details'}
              size="small"
              variant="outlined"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'inherit',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            {showDetails && (
              <Box sx={{ 
                mt: 1, 
                p: 1, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                maxHeight: 100,
                overflow: 'auto',
              }}>
                {notification.details}
              </Box>
            )}
          </Box>
        )}
      </Alert>
    </Slide>
  );
};

// Hook to use notifications
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Utility functions for quick access
export const showGlobalNotification = (
  message: string, 
  type: 'success' | 'error' | 'warning' | 'info' = 'info',
  title?: string,
  details?: string
) => {
  document.dispatchEvent(new CustomEvent('showNotification', {
    detail: { message, type, title, details }
  }));
};

export const showGlobalSuccess = (message: string, title?: string) => {
  showGlobalNotification(message, 'success', title);
};

export const showGlobalError = (message: string, title?: string, details?: string) => {
  showGlobalNotification(message, 'error', title, details);
};

export const showGlobalWarning = (message: string, title?: string) => {
  showGlobalNotification(message, 'warning', title);
};

export const showGlobalInfo = (message: string, title?: string) => {
  showGlobalNotification(message, 'info', title);
};

export default NotificationProvider; 